import {
    GridValueFormatterParams,
    GridValueGetterParams,
} from '@mui/x-data-grid';
import { deviceHealthFormatter } from 'components/DataGridCells/data-grid-formatters';
import { deviceHealthSorter } from 'components/DataGridCells/data-grid-sorters';
import DeviceBatteryIcon from 'components/Devices/DeviceBatteryIcon/DeviceBatteryIcon';
import DeviceHealthIcon from 'components/Devices/DeviceHealthIcon/DeviceHealthIcon';
import { parse } from 'date-fns';
import { getBatteryLevel } from 'helpers/general';
import {
    DeviceHealth,
    RawSupplierStats,
    SupplierStat,
    ConnectivityTrendGraphSeries,
    StatusTrendGraphSeries,
    DeviceLastReading,
} from 'types';
import { getNextSteps } from './device-status';
import { DataTableProps } from 'components/data/DataTable/DataTable';

export const getNextStepsFormatter = (
    params: GridValueFormatterParams<string>
) => {
    return getNextSteps(params.value);
};

export const lowBatteryColumns: DataTableProps['columns'] = [
    {
        headerName: 'Battery',
        field: 'lastReading',
        sortable: false,
        sortComparator: (v: DeviceLastReading, v2: DeviceLastReading) => {
            if (v.battery && !v2.battery) {
                return 1;
            } else if (!v.battery && v2.battery) {
                return -1;
            } else if (v.battery && v2.battery) {
                return v.battery > v2.battery ? -1 : 1;
            }
            return 0;
        },
        renderCell: (params) => {
            return (
                <DeviceBatteryIcon
                    battery={params.row}
                    itemNumber={params.row.item_number}
                />
            );
        },
        valueGetter: (params: GridValueFormatterParams<DeviceLastReading>) => {
            if (params.value === undefined) return 0;
            const { battery_crit, battery_warn } = params.value;

            const batteryLevel = getBatteryLevel(battery_crit, battery_warn);

            return batteryLevel === 'unknown' ? 0 : batteryLevel;
        },
    },
    {
        field: 'health',
        headerName: 'Health',
        renderCell: (params) => {
            const data = params.value || {
                icon: '',
                color: '',
                tooltip: '',
            };
            return <DeviceHealthIcon {...data} />;
        },
        sortingOrder: ['asc', 'desc'],
        valueFormatter: (params) => deviceHealthFormatter(params),
        sortComparator: (v: DeviceHealth, v2: DeviceHealth) =>
            deviceHealthSorter(v, v2),
    },
    {
        field: 'shortDeviceId',
        headerName: 'Device ID',
        sortingOrder: ['asc', 'desc'],
    },
    {
        field: 'name',
        headerName: 'Monitor',
        flex: 1,
        sortingOrder: ['asc', 'desc'],
    },
    {
        field: 'customer_name',
        headerName: 'Customer Name',
        flex: 1,
        sortingOrder: ['asc', 'desc'],
    },
    {
        headerName: 'Address',
        field: 'address',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => {
            if (params.value) return params.value;
            return 'No Address Entered';
        },
    },
];

/** Columns for data grid table showing offline (which are necessarily deployed) devices. */
export const offlineColumns: DataTableProps['columns'] = [
    {
        field: 'health',
        headerName: 'Health',
        renderCell: (params) => {
            const data = params.value || {
                icon: '',
                color: '',
                tooltip: '',
            };
            return <DeviceHealthIcon {...data} />;
        },
        sortingOrder: ['asc', 'desc'],
        valueFormatter: (params) => deviceHealthFormatter(params),
        sortComparator: (v: DeviceHealth, v2: DeviceHealth) =>
            deviceHealthSorter(v, v2),
    },
    {
        field: 'shortDeviceId',
        headerName: 'Device ID',
        sortingOrder: ['asc', 'desc'],
    },
    {
        field: 'name',
        headerName: 'Monitor',
        flex: 1,
        sortingOrder: ['asc', 'desc'],
    },
    {
        headerName: 'Address',
        field: 'address',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => {
            if (params.value) return params.value;
            return 'No Address Entered';
        },
    },
    {
        field: 'customer_name',
        headerName: 'Customer Name',
        flex: 1,
        sortingOrder: ['asc', 'desc'],
    },
    {
        field: 'status',
        headerName: 'Next Steps',
        sortingOrder: ['asc', 'desc'],
        valueFormatter: getNextStepsFormatter,
    },
];

/** Columns for data grid table showing sleeping devices. */
export const sleepColumns: DataTableProps['columns'] = [
    {
        field: 'shortDeviceId',
        headerName: 'Device ID',
    },
    {
        field: 'name',
        headerName: 'Monitor',
        flex: 1,
    },
    {
        headerName: 'Address',
        field: 'address',
        flex: 1,
        valueGetter: (params: GridValueGetterParams) => {
            if (params.value) return params.value;
            return 'No Address Entered';
        },
    },
    {
        field: 'customer_name',
        headerName: 'Customer Name',
        flex: 1,
        sortingOrder: ['asc', 'desc'],
    },
    {
        field: 'status',
        headerName: 'Next Steps',
        valueFormatter: getNextStepsFormatter,
    },
];

/** Produce correctly formatted ConnectivityTrendGraph data for use with highcharts from raw supplier stats.  */
export const processSupplierConnectivityStats = async (
    stats: RawSupplierStats
): Promise<ConnectivityTrendGraphSeries> => {
    const graph: ConnectivityTrendGraphSeries = {
        connected: {
            data: [],
        },
        disconnected: {
            data: [],
        },
    };
    const promises = [];
    for (const i in stats) {
        promises.push(async () => {
            const stat = stats[i];
            const timestamp = parse(
                i.toString(),
                'yyyyMMddHH',
                new Date()
            ).getTime();
            return parseConnectivityTrendData(timestamp, stat, graph);
        });
    }
    for (const promise of promises) promise();
    await Promise.all(promises);
    graph.connected.data.sort((a, b) => {
        return a[0] - b[0];
    });
    graph.disconnected.data.sort((a, b) => {
        return a[0] - b[0];
    });
    return graph;
};

/** Add correctly formatted connectivity trend data to the graph object.*/
const parseConnectivityTrendData = (
    timestamp: number,
    stat: SupplierStat,
    graph: ConnectivityTrendGraphSeries
): ConnectivityTrendGraphSeries => {
    if (Number(stat.connectPct) >= 0) {
        // multiply value by 100 so that 0.5 connectPct is displayed as 50% etc
        graph.connected.data.push([timestamp, Number(stat.connectPct) * 100]);
    }
    if (Number(stat.offlinePct) >= 0) {
        // multiply value by 100 so that 0.5 offlinePct is displayed as 50% etc
        graph.disconnected.data.push([
            timestamp,
            Number(stat.offlinePct) * 100,
        ]);
    }

    return graph;
};

/** Produce correctly formatted StatusTrendGraphSeries data for use with highcharts from raw supplier stats.  */
export const processSupplierStatusStats = async (
    stats: RawSupplierStats,
    isInternalUser: boolean
): Promise<StatusTrendGraphSeries> => {
    const graph: StatusTrendGraphSeries = {
        deployed: {
            data: [],
        },
        shipped: {
            data: [],
        },
        sleep: {
            data: [],
        },
    };

    // Only show retired monitors to internal users.
    if (isInternalUser) {
        graph.retired = { data: [] };
    }

    const promises = [];

    for (const i in stats) {
        promises.push(async () => {
            const stat = stats[i];
            const timestamp = parse(
                i.toString(),
                'yyyyMMddHH',
                new Date()
            ).getTime();
            return parseStatusTrendData(timestamp, stat, graph, isInternalUser);
        });
    }

    for (const promise of promises) promise();
    await Promise.all(promises);

    // Make sure data points are correctly ordered.
    graph.deployed.data.sort((a, b) => {
        return a[0] - b[0];
    });
    graph.shipped.data.sort((a, b) => {
        return a[0] - b[0];
    });
    graph.sleep.data.sort((a, b) => {
        return a[0] - b[0];
    });

    if (graph.retired) {
        graph.retired.data.sort((a, b) => {
            return a[0] - b[0];
        });
    }

    return graph;
};

/** Add correctly formatted status trend data to the graph object.*/
const parseStatusTrendData = (
    timestamp: number,
    stat: SupplierStat,
    graph: StatusTrendGraphSeries,
    isInternalUser: boolean
): StatusTrendGraphSeries => {
    if (stat?.status?.deployed) {
        graph.deployed.data.push([timestamp, stat.status.deployed]);
    } else {
        graph.deployed.data.push([timestamp, 0]);
    }

    if (stat?.status?.shipped) {
        graph.shipped.data.push([timestamp, stat.status.shipped]);
    } else {
        graph.shipped.data.push([timestamp, 0]);
    }

    if (stat?.status?.sleep) {
        graph.sleep.data.push([timestamp, stat.status.sleep]);
    } else {
        graph.sleep.data.push([timestamp, 0]);
    }

    // Only show retired monitors to internal users.
    if (isInternalUser && graph.retired) {
        if (stat?.status?.retired) {
            graph.retired.data.push([timestamp, stat.status.retired]);
        } else {
            graph.retired.data.push([timestamp, 0]);
        }
    }

    return graph;
};
